import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Car, Award, Star } from 'lucide-react'
import { getPeriodLabel, getPreviousPeriodLabel } from '@/core/shared'
import { Period } from '@/core/shared/types/dashboard'

interface MetricsCardsProps {
  currentPeriodData: {
    ridesCompleted: number
    commission: number
    rating: string
  } | null
  previousPeriodData: {
    ridesCompleted: number
    commission: number
    rating: string
  }
  selectedPeriod: Period
}

export default function MetricsCards({
  currentPeriodData,
  previousPeriodData,
  selectedPeriod,
}: MetricsCardsProps) {
  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return '0.00'
    const growth = ((current - previous) / previous) * 100
    return growth.toFixed(2)
  }

  const getGrowthColor = (growth: number) => {
    return growth >= 0 ? 'text-green-600' : 'text-red-600'
  }

  const getGrowthIcon = (growth: number) => {
    return growth >= 0 ? (
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 10l7-7m0 0l7 7m-7-7v18"
        />
      </svg>
    ) : (
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 14l-7 7m0 0l-7-7m7 7V3"
        />
      </svg>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Corridas {getPeriodLabel(selectedPeriod)}
          </CardTitle>
          <Car className="h-5 w-5 text-[hsl(var(--brand-color))]" />
        </CardHeader>
        <CardContent>
          <div className="text-xl font-bold">
            {currentPeriodData?.ridesCompleted || 0}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {getGrowthIcon(
              parseFloat(
                calculateGrowth(
                  currentPeriodData?.ridesCompleted || 0,
                  previousPeriodData.ridesCompleted
                )
              )
            )}
            <span
              className={getGrowthColor(
                parseFloat(
                  calculateGrowth(
                    currentPeriodData?.ridesCompleted || 0,
                    previousPeriodData.ridesCompleted
                  )
                )
              )}
            >
              {calculateGrowth(
                currentPeriodData?.ridesCompleted || 0,
                previousPeriodData.ridesCompleted
              )}
              %
            </span>
            <span>{getPreviousPeriodLabel(selectedPeriod)}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Comissão Gerada</CardTitle>
          <Award className="h-5 w-5 text-[hsl(var(--brand-color))]" />
        </CardHeader>
        <CardContent>
          <div className="text-xl font-bold text-[hsl(var(--brand-color))]">
            R$ {(currentPeriodData?.commission || 0).toFixed(2)}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {getGrowthIcon(
              parseFloat(
                calculateGrowth(
                  currentPeriodData?.commission || 0,
                  previousPeriodData.commission
                )
              )
            )}
            <span
              className={getGrowthColor(
                parseFloat(
                  calculateGrowth(
                    currentPeriodData?.commission || 0,
                    previousPeriodData.commission
                  )
                )
              )}
            >
              {calculateGrowth(
                currentPeriodData?.commission || 0,
                previousPeriodData.commission
              )}
              %
            </span>
            <span>{getPreviousPeriodLabel(selectedPeriod)}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Avaliação</CardTitle>
          <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
        </CardHeader>
        <CardContent>
          <div className="text-xl font-bold">
            {currentPeriodData?.rating || '0'}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {getGrowthIcon(
              parseFloat(
                calculateGrowth(
                  parseFloat(currentPeriodData?.rating || '0'),
                  parseFloat(previousPeriodData.rating || '0')
                )
              )
            )}
            <span
              className={getGrowthColor(
                parseFloat(
                  calculateGrowth(
                    parseFloat(currentPeriodData?.rating || '0'),
                    parseFloat(previousPeriodData.rating || '0')
                  )
                )
              )}
            >
              {calculateGrowth(
                parseFloat(currentPeriodData?.rating || '0'),
                parseFloat(previousPeriodData.rating || '0')
              )}
              %
            </span>
            <span>{getPreviousPeriodLabel(selectedPeriod)}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
